import { useCallback, useEffect, useState } from "react";
import { Users, Plus, Trash2, Wallet, ArrowDownToLine, Loader2, Receipt } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { StockPanel } from "@/components/StockPanel";

const inputCls = "bg-[#161926] border-amber-500/20 text-slate-100";
const err = (e) => toast.error(formatApiError(e.response?.data?.detail));
const fmtDate = (iso) => new Date(iso).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });

function DriversList({ drivers, reload }) {
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const add = async () => {
    if (!form.name || !form.email || !form.password) { toast.error("Tous les champs requis"); return; }
    try { await api.post("/admin/drivers", form); toast.success("Livreur créé"); setForm({ name: "", email: "", password: "" }); reload(); } catch (e) { err(e); }
  };
  const del = async (id) => {
    if (!window.confirm("Supprimer ce livreur ?")) return;
    try { await api.delete(`/admin/drivers/${id}`); toast.success("Supprimé"); reload(); } catch (e) { err(e); }
  };
  const toggle = async (d) => {
    try {
      await api.patch(`/admin/drivers/${d.id}/active`, { active: !d.active });
      toast.success(d.active ? `${d.name} désactivé — ne reçoit plus les commandes` : `${d.name} activé`);
    } catch (e) { err(e); }
    reload();
  };
  return (
    <div className="glass rounded-2xl p-5 mb-5">
      <h3 className="text-lg font-semibold text-slate-200 mb-3 flex items-center gap-2"><Users className="w-4 h-4 text-amber-400" /> Livreurs</h3>
      <div className="space-y-2 mb-4">
        {drivers.map((d) => (
          <div key={d.id} className="flex items-center justify-between bg-[#161926] rounded-lg p-3 border border-amber-500/10">
            <div><p className="text-sm text-slate-100">{d.name} <span data-testid={`driver-status-${d.id}`} className={`ml-1 text-[10px] font-mono uppercase px-1.5 py-0.5 rounded-full ${d.active ? "bg-emerald-500/15 text-emerald-300" : "bg-rose-500/15 text-rose-300"}`}>{d.active ? "Actif" : "Inactif"}</span></p><p className="text-xs text-slate-500">{d.email} · {d.active_orders} en cours</p></div>
            <div className="flex items-center gap-3">
              <Switch data-testid={`toggle-driver-${d.id}`} checked={d.active} onCheckedChange={() => toggle(d)} />
              <button data-testid={`delete-driver-${d.id}`} onClick={() => del(d.id)} className="text-rose-400"><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
        {drivers.length === 0 && <p className="text-sm text-slate-500 text-center py-3">Aucun livreur.</p>}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        <Input data-testid="new-driver-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Nom" className={inputCls} />
        <Input data-testid="new-driver-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email" className={inputCls} />
        <Input data-testid="new-driver-password" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Mot de passe" className={inputCls} />
      </div>
      <Button data-testid="add-driver-button" onClick={add} className="btn-gold rounded-xl mt-3"><Plus className="w-4 h-4 mr-1" /> Ajouter un livreur</Button>
    </div>
  );
}

function DriverCash({ driver, reload }) {
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async (value, kind) => {
    const amt = parseFloat(String(value).replace(",", "."));
    if (!amt || amt <= 0) { toast.error("Montant invalide"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/drivers/${driver.id}/${kind}`, { amount: amt });
      toast.success(`${kind === "deposit" ? "Ajout" : "Retrait"} de ${amt.toFixed(2)} € — ${driver.name} détient ${data.cash_float.toFixed(2)} €`);
      setAmount(""); reload();
    } catch (e) { err(e); } finally { setBusy(false); }
  };
  const withdraw = (value) => send(value, "withdraw");
  return (
    <div data-testid={`driver-cash-${driver.id}`} className="bg-[#161926] rounded-xl p-4 border border-amber-500/10">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-100 font-semibold">{driver.name}</p>
        <p className="font-mono text-xl gold-text font-bold" data-testid={`driver-cash-amount-${driver.id}`}>{driver.cash_float.toFixed(2)} €</p>
      </div>
      <div className="flex flex-wrap items-center gap-2 mt-3">
        <Input data-testid={`driver-withdraw-input-${driver.id}`} inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="Montant €" className={`${inputCls} font-mono flex-1 min-w-[8rem] h-9`} />
        <Button data-testid={`driver-withdraw-button-${driver.id}`} disabled={busy} onClick={() => withdraw(amount)} className="btn-gold rounded-xl h-11 px-5 text-base font-semibold">
          <ArrowDownToLine className="w-5 h-5 mr-1.5" /> Retrait
        </Button>
        <Button data-testid={`driver-deposit-button-${driver.id}`} disabled={busy} onClick={() => send(amount, "deposit")} variant="outline" size="sm" className="rounded-lg h-8 px-2.5 text-xs border-amber-500/30 text-amber-200 hover:bg-amber-500/10">
          <Plus className="w-3.5 h-3.5 mr-1" /> Ajout
        </Button>
        {driver.cash_float > 0 && (
          <button data-testid={`driver-withdraw-all-${driver.id}`} disabled={busy} onClick={() => window.confirm(`Retirer les ${driver.cash_float.toFixed(2)} € détenus par ${driver.name} ?`) && withdraw(driver.cash_float)} className="text-xs text-slate-400 hover:text-amber-300 underline">Tout retirer</button>
        )}
      </div>
    </div>
  );
}

function CashPanel({ cash, reload }) {
  return (
    <div className="glass rounded-2xl p-5 border border-amber-500/15">
      <h3 className="text-lg font-semibold text-slate-200 mb-3 flex items-center gap-2"><Wallet className="w-4 h-4 text-amber-400" /> Fond de caisse</h3>
      <div className="rounded-xl bg-amber-500/10 border border-amber-500/30 p-4 mb-4">
        <p className="text-[11px] font-mono uppercase tracking-wider text-slate-400">Total détenu par les livreurs</p>
        <p className="text-3xl font-bold gold-text font-mono" data-testid="drivers-cash-total">{cash.total.toFixed(2)} €</p>
        <p className="text-xs text-slate-500 mt-1">Espèces encaissées à la livraison, pas encore remises.</p>
      </div>
      <div className="space-y-3">
        {cash.drivers.map((d) => <DriverCash key={d.id} driver={d} reload={reload} />)}
      </div>
      <div className="pt-4 mt-4 border-t border-amber-500/10">
        <p className="text-[11px] font-mono uppercase tracking-wider text-slate-500 mb-2">Historique des mouvements</p>
        {cash.movements.length === 0 ? <p className="text-xs text-slate-500">Aucun mouvement pour le moment.</p> : (
          <div className="space-y-0.5" data-testid="withdrawals-history">
            {cash.movements.map((m, i) => (
              <div key={i} className="flex items-center justify-between text-xs py-1.5 border-b border-amber-500/5 last:border-0">
                <span className="text-slate-400">{m.driver_name || "Livreur"}{m.note ? ` · ${m.note}` : ""}<span className="text-slate-600 ml-2 font-mono">{fmtDate(m.created_at)}</span></span>
                {m.type === "deposit"
                  ? <span className="font-mono text-amber-300">+{m.amount.toFixed(2)} €</span>
                  : <span className="font-mono text-emerald-300">-{m.amount.toFixed(2)} €</span>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function PayForm({ driver, reload }) {
  const [v, setV] = useState("");
  const [busy, setBusy] = useState(false);
  const pay = async () => {
    const a = parseFloat(v.replace(",", "."));
    if (!a || a <= 0) { toast.error("Montant invalide"); return; }
    if (a > driver.remaining + 0.001 && !window.confirm(`${a.toFixed(2)} € dépasse le reste à payer (${driver.remaining.toFixed(2)} €). Enregistrer quand même (avance) ?`)) return;
    setBusy(true);
    try { await api.post(`/admin/drivers/${driver.id}/pay`, { amount: a }); toast.success(`Paiement de ${a.toFixed(2)} € enregistré pour ${driver.name}`); setV(""); reload(); }
    catch (e) { err(e); } finally { setBusy(false); }
  };
  return (
    <div className="mt-3 pt-3 border-t border-amber-500/10">
      <div className="grid grid-cols-2 gap-2 text-center mb-2">
        <div><p className="text-[10px] uppercase text-slate-500">Déjà payé</p><p className="font-mono text-slate-200" data-testid={`recap-paid-${driver.id}`}>{driver.paid.toFixed(2)} €</p></div>
        <div><p className="text-[10px] uppercase text-slate-500">Reste à payer</p><p className={`font-mono font-bold ${driver.remaining > 0 ? "text-rose-300" : "text-emerald-300"}`} data-testid={`recap-remaining-${driver.id}`}>{driver.remaining.toFixed(2)} €</p></div>
      </div>
      <div className="flex gap-2">
        <Input data-testid={`recap-pay-input-${driver.id}`} inputMode="decimal" value={v} onChange={(e) => setV(e.target.value)} placeholder="Montant payé €" className={`${inputCls} font-mono h-9 flex-1`} />
        <Button data-testid={`recap-pay-button-${driver.id}`} disabled={busy} onClick={pay} className="btn-gold rounded-xl h-9">Enregistrer</Button>
      </div>
    </div>
  );
}

function RecapPanel({ recap, reload }) {
  return (
    <div className="glass rounded-2xl p-5 mb-5 border border-amber-500/15" data-testid="drivers-recap">
      <h3 className="text-lg font-semibold text-slate-200 mb-1 flex items-center gap-2"><Receipt className="w-4 h-4 text-amber-400" /> Récapitulatif financier</h3>
      <p className="text-[12px] text-slate-400 mb-4">Commandes livrées depuis la dernière remise à zéro du mois.</p>
      <div className="grid grid-cols-3 gap-2 rounded-xl bg-amber-500/10 border border-amber-500/30 p-3 mb-3 text-center">
        <div><p className="text-[10px] uppercase text-slate-400">Livrées</p><p className="font-mono text-lg font-bold text-slate-100" data-testid="recap-total-delivered">{recap.totals.delivered}</p></div>
        <div><p className="text-[10px] uppercase text-slate-400">Encaissé</p><p className="font-mono text-lg font-bold gold-text" data-testid="recap-total-collected">{recap.totals.collected.toFixed(2)} €</p></div>
        <div><p className="text-[10px] uppercase text-slate-400">Paie</p><p className="font-mono text-lg font-bold text-emerald-300" data-testid="recap-total-pay">{recap.totals.pay.toFixed(2)} €</p></div>
        <div className="col-span-3 text-xs text-slate-300">Déjà payé <span className="font-mono">{recap.totals.paid.toFixed(2)} €</span> · Reste à payer <span className="font-mono font-bold text-rose-300" data-testid="recap-total-remaining">{recap.totals.remaining.toFixed(2)} €</span></div>
      </div>
      <div className="space-y-2">
        {recap.drivers.map((d) => (
          <div key={d.id} data-testid={`recap-driver-${d.id}`} className="bg-[#161926] rounded-xl p-3 border border-amber-500/10">
            <p className="text-sm font-semibold text-slate-100 mb-2">{d.name}</p>
            <div className="grid grid-cols-3 gap-2 text-center">
              <div><p className="text-[10px] uppercase text-slate-500">Livrées</p><p className="font-mono text-slate-100" data-testid={`recap-delivered-${d.id}`}>{d.delivered}</p></div>
              <div><p className="text-[10px] uppercase text-slate-500">Encaissé</p><p className="font-mono text-amber-300" data-testid={`recap-collected-${d.id}`}>{d.collected.toFixed(2)} €</p><p className="text-[9px] text-slate-500">esp. {d.cash.toFixed(2)} · CB {d.card.toFixed(2)}</p></div>
              <div><p className="text-[10px] uppercase text-slate-500">Paie</p><p className="font-mono text-emerald-300 font-bold" data-testid={`recap-pay-${d.id}`}>{d.pay.toFixed(2)} €</p></div>
            </div>
            <PayForm driver={d} reload={reload} />
          </div>
        ))}
      </div>
    </div>
  );
}

export const DriversTab = () => {
  const [drivers, setDrivers] = useState(null);
  const [cash, setCash] = useState(null);
  const [stock, setStock] = useState(null);
  const [recap, setRecap] = useState(null);
  const [failed, setFailed] = useState(false);
  const load = useCallback(() => {
    setFailed(false);
    const fail = () => setFailed(true);
    api.get("/admin/drivers").then((r) => setDrivers(r.data)).catch(fail);
    api.get("/admin/drivers/cash").then((r) => setCash(r.data)).catch(fail);
    api.get("/admin/drivers/stock").then((r) => setStock(r.data)).catch(fail);
    api.get("/admin/drivers/recap").then((r) => setRecap(r.data)).catch(fail);
  }, []);
  useEffect(() => { load(); }, [load]);
  if (failed) return <div className="text-center py-20"><p className="text-slate-400 mb-3">Impossible de charger les livreurs.</p><Button data-testid="drivers-retry" onClick={load} className="btn-gold rounded-xl">Réessayer</Button></div>;
  if (!drivers || !cash || !stock || !recap) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 text-amber-400 animate-spin" /></div>;
  return (
    <div className="fade-up max-w-2xl" data-testid="drivers-tab">
      <h2 className="text-3xl font-bold text-slate-100 mb-1">Livreurs</h2>
      <p className="text-slate-400 text-sm mb-6">Équipe de livraison, espèces et stock détenus par chaque livreur.</p>
      <DriversList drivers={drivers} reload={load} />
      <RecapPanel recap={recap} reload={load} />
      <CashPanel cash={cash} reload={load} />
      <StockPanel stock={stock} reload={load} />
    </div>
  );
};
